import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Archive, Banknote, CalendarPlus, Camera, Package, PenLine, Trash2, Wand2, Wallet } from 'lucide-react';
import { apiGet } from '../../../api/client';
import type { Event, EventPayment } from '../../../api/types';
import { formatDate, formatDateTime, formatCurrency, formatPaymentMethod } from '../../../utils/format';

interface Milestone {
  key: string;
  label: string;
  date?: string | null;
  icon: React.ReactNode;
  meta?: string;
  subMeta?: React.ReactNode;
}

export default function TimelineTab({ event }: { event: Event }) {
  const { data: payments = [] } = useQuery({
    queryKey: ['event-payments', event.id],
    queryFn: () => apiGet(`/events/${event.id}/payments`),
    enabled: !!event.id,
  });

  const paymentsList = (payments as any[]).length > 0
    ? (payments as any[])
    : (event.depositPaidAt
      ? [{ id: 'legacy-deposit', amount: Number(event.depositAmount ?? 0), paidAt: event.depositPaidAt!, method: event.depositPaidMethod ?? 'CONTANTI' }]
      : []).concat((event.finalPaidAt
        ? [{ id: 'legacy-final', amount: Number(event.finalAmount ?? 0), paidAt: event.finalPaidAt!, method: event.finalPaidMethod ?? 'CONTANTI' }]
        : []));

  const paymentsSubMeta = event.contractSignedAt && paymentsList.length > 0 ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.15rem' }}>
      {paymentsList.map((p: any) => (
        <span key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 500 }}>
          <Banknote size={11} />
          {formatCurrency(Number(p.amount))} · {formatDate(p.paidAt)} · {formatPaymentMethod(p.method)}
        </span>
      ))}
    </div>
  ) : null;

  const milestones: Milestone[] = [
    {
      key: 'created',
      label: 'Evento creato',
      date: event.createdAt,
      icon: <CalendarPlus size={15} />,
    },
    {
      key: 'contract',
      label: 'Contratto firmato',
      date: event.contractSignedAt,
      icon: <PenLine size={15} />,
      subMeta: paymentsSubMeta,
    },
    {
      key: 'shooting',
      label: 'Shooting completato',
      date: event.shootingDoneAt,
      icon: <Camera size={15} />,
    },
    {
      key: 'editing',
      label: 'Post-produzione iniziata',
      date: event.editingStartedAt,
      icon: <Wand2 size={15} />,
    },
    {
      key: 'delivered',
      label: 'Consegnato al cliente',
      date: event.deliveredAt,
      icon: <Package size={15} />,
    },
    {
      key: 'archived',
      label: 'Archiviato',
      date: event.archivedAt,
      icon: <Archive size={15} />,
    },
    ...(event.originalsDeletedAt
      ? [{ key: 'originals', label: 'Originali eliminati', date: event.originalsDeletedAt, icon: <Trash2 size={15} /> }]
      : []),
  ];

  // Only keep items that are either done OR are pending steps up to the next completed one
  // We show all items always, but visually differentiate done vs pending
  const lastDoneIndex = milestones.reduce((last, m, i) => (m.date ? i : last), -1);

  return (
    <div style={{
      background: 'var(--glass-bg-card)',
      border: '1px solid rgba(255,255,255,0.10)',
      borderRadius: 'var(--radius-card)',
      padding: '1.5rem 1.75rem',
    }}>
      <h3 style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        color: 'var(--color-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: '1.5rem',
      }}>
        Timeline — {event.title}
      </h3>

      <div>
        {milestones.map((m, i) => {
          const done = !!m.date;
          const isLast = i === milestones.length - 1;
          // Line segment color: filled if this AND next step are both done
          const nextDone = i < milestones.length - 1 && !!milestones[i + 1].date;
          const lineColor = done && nextDone
            ? 'var(--color-primary)'
            : 'rgba(255,255,255,0.10)';

          return (
            <div key={m.key} style={{ display: 'flex', gap: '1rem' }}>
              {/* Left track: dot + connector line */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 36, flexShrink: 0 }}>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: done ? 'var(--color-primary)' : 'var(--glass-bg-card)',
                  border: `2px solid ${done ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`,
                  color: done ? '#fff' : 'var(--color-text-faint)',
                  flexShrink: 0,
                  transition: 'background var(--transition-interactive), border-color var(--transition-interactive)',
                }}>
                  {m.icon}
                </div>
                {!isLast && (
                  <div style={{
                    flex: 1,
                    width: 2,
                    minHeight: '1.75rem',
                    background: lineColor,
                    transition: 'background var(--transition-interactive)',
                  }} />
                )}
              </div>

              {/* Right: label + date / pending */}
              <div style={{ paddingTop: '0.5rem', paddingBottom: isLast ? 0 : '0.5rem', flex: 1 }}>
                <p style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: done ? 600 : 400,
                  color: done ? 'var(--color-text)' : 'var(--color-text-faint)',
                  marginBottom: '0.125rem',
                }}>
                  {m.label}
                </p>
                {done ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {formatDateTime(m.date!)}
                    </span>
                    {m.meta && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 500 }}>
                        {m.meta}
                      </span>
                    )}
                    {m.subMeta}
                  </div>
                ) : (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                    In attesa
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary: completed count */}
      <div style={{
        marginTop: '1.5rem',
        paddingTop: '1rem',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
          {lastDoneIndex + 1} di {milestones.length} step completati
        </span>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
          Aggiornato {formatDate(event.updatedAt)}
        </span>
      </div>
    </div>
  );
}
